import "@/App.css";
import "@/explosion-brand.css";
import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/auth";
import { lazyWithRetry as lazy } from "@/lazyWithRetry";
import { NotificationProvider } from "@/notifications";
import { ThemeProvider } from "@/theme";
import Shell from "@/components/Shell";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";

const BillingPage = lazy(() => import("@/pages/BillingV2"));
const BillingArchivePage = lazy(() => import("@/pages/BillingArchive"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesV2"));
const PaymentTermsEditorPage = lazy(() => import("@/pages/PaymentTermsEditor"));
const ImportExportPage = lazy(() => import("@/pages/ImportExportV3"));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePassword"));
const UsersPage = lazy(() => import("@/pages/UsersV2"));
const MyDocumentsPage = lazy(() => import("@/pages/DocumentsV2"));
const RoleSelectionPage = lazy(() => import("@/pages/RoleSelection"));
const AvailabilityPage = lazy(() => import("@/pages/AvailabilityV2"));
const AuditLogPage = lazy(() => import("@/pages/AuditLog"));
const EmailLogPage = lazy(() => import("@/pages/EmailLog"));
const ReportsPage = lazy(() => import("@/pages/Reports"));
const CompensationsPage = lazy(() => import("@/pages/Compensations"));
const TreasuryPage = lazy(() => import("@/pages/Treasury"));
const GuidePage = lazy(() => import("@/pages/Guide"));
const CalendarPage = lazy(() => import("@/pages/CalendarView"));
const ClientEditorPage = lazy(() => import("@/pages/ClientEditor"));
const ClientHistoryPage = lazy(() => import("@/pages/ClientHistory"));
const ClientsPage = lazy(() => import("@/pages/ClientsV2"));
const SuppliersPage = lazy(() => import("@/pages/SuppliersPage"));
const SupplierInvoicesPage = lazy(() => import("@/pages/SupplierInvoicesPage"));
const FeedbackPage = lazy(() => import("@/pages/FeedbackPage"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage"));
const DashboardPage = lazy(() => import("@/pages/Dashboard"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsV2"));
const QuoteAcceptancePage = lazy(() => import("@/pages/QuoteAcceptance"));
const PublicPrivateRequestPage = lazy(() => import("@/pages/PublicPrivateRequest"));
const PublicCompanyRequestPage = lazy(() => import("@/pages/PublicCompanyRequest"));
const InvoiceUploadPage = lazy(() => import("@/pages/InvoiceUpload"));
const QuotesPage = lazy(() => import("@/pages/QuotesV3"));
const QuoteArchivePage = lazy(() => import("@/pages/QuoteArchive"));
const QuoteEditorPage = lazy(() => import("@/pages/QuoteEditor"));
const QuoteTaskPlannerPage = lazy(() => import("@/pages/QuoteTaskPlannerPage"));
const CatalogPage = lazy(() => import("@/pages/Catalog"));
const ServiceEditorPage = lazy(() => import("@/pages/ServiceEditor"));
const KitEditorPage = lazy(() => import("@/pages/KitEditor"));
const TasksPage = lazy(() => import("@/pages/TasksV2"));
const TaskArchivePage = lazy(() => import("@/pages/TaskArchive"));
const TaskEditorPage = lazy(() => import("@/pages/TaskEditor"));
const UserEditorPage = lazy(() => import("@/pages/UserEditor"));
const ProfilePage = lazy(() => import("@/pages/Profile"));
const WorkloadPage = lazy(() => import("@/pages/WorkloadV2"));
const DownloadAppPage = lazy(() => import("@/pages/DownloadApp"));
const MobileAppAdminPage = lazy(() => import("@/pages/MobileAppAdmin"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicy"));
const SupplierPortalPage = lazy(() => import("@/pages/SupplierPortal"));
const CompetenzePage = lazy(() => import("@/pages/CompetenzePage"));
const ActionHubPage = lazy(() => import("@/pages/ActionHub"));
const TwintPage = lazy(() => import("@/pages/TwintPage"));
const AccountingLedgerPage = lazy(() => import("@/pages/AccountingLedger"));
const AssistantPage = lazy(() => import("@/pages/AssistantPage"));

function PageFallback() {
  return <div className="loading-screen" data-testid="page-loading">Caricamento…</div>;
}

function ProtectedRoute() {
  const { user, checking } = useAuth();
  if (checking) return <div className="loading-screen" data-testid="session-loading">Caricamento spazio di lavoro…</div>;
  return user ? (user.force_password_change ? <Navigate to="/cambia-password" replace /> : <Shell />) : <Navigate to="/login" replace />;
}

function PasswordRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="loading-screen">Caricamento…</div>;
  return user ? (children || <ChangePasswordPage />) : <Navigate to="/login" replace />;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  return roles.includes(user.role) ? children : <Navigate to="/" replace />;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <NotificationProvider>
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reimposta-password" element={<ResetPassword />} />
          <Route path="/accetta/:token" element={<QuoteAcceptancePage />} />
          <Route path="/richiesta-privati" element={<PublicPrivateRequestPage />} />
          <Route path="/richiesta-aziende" element={<PublicCompanyRequestPage />} />
          <Route path="/carica-fattura/:token" element={<InvoiceUploadPage />} />
          <Route path="/scarica-app" element={<DownloadAppPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/fornitore/:token" element={<SupplierPortalPage />} />
          <Route path="/cambia-password" element={<PasswordRoute />} />
          <Route path="/scegli-profilo" element={<PasswordRoute><RoleSelectionPage /></PasswordRoute>} />
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/assistente" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><AssistantPage /></RoleRoute>} />
            <Route path="/calendario" element={<CalendarPage />} />
            <Route path="/clienti" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><ClientsPage /></RoleRoute>} />
            <Route path="/fornitori" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><SuppliersPage /></RoleRoute>} />
            <Route path="/fatture-fornitori" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><SupplierInvoicesPage /></RoleRoute>} />
            <Route path="/lead" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><LeadsPage /></RoleRoute>} />
            <Route path="/clienti/nuovo" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><ClientEditorPage /></RoleRoute>} />
            <Route path="/clienti/:clientId/modifica" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><ClientEditorPage /></RoleRoute>} />
            <Route path="/clienti/:clientId/storico" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><ClientHistoryPage /></RoleRoute>} />
            <Route path="/listino" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><CatalogPage /></RoleRoute>} />
            <Route path="/listino/nuovo" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><ServiceEditorPage /></RoleRoute>} />
            <Route path="/listino/:serviceId/modifica" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><ServiceEditorPage /></RoleRoute>} />
            <Route path="/listino/kit/nuovo" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><KitEditorPage /></RoleRoute>} />
            <Route path="/listino/kit/:kitId/modifica" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><KitEditorPage /></RoleRoute>} />
            <Route path="/magazzino" element={<Navigate to="/listino?tab=magazzino" replace />} />
            <Route path="/preventivi" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><QuotesPage /></RoleRoute>} />
            <Route path="/preventivi/archivio" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><QuoteArchivePage /></RoleRoute>} />
            <Route path="/preventivi/nuovo" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><QuoteEditorPage /></RoleRoute>} />
            <Route path="/preventivi/:quoteId/modifica" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><QuoteEditorPage /></RoleRoute>} />
            <Route path="/preventivi/:quoteId/task" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><QuoteTaskPlannerPage /></RoleRoute>} />
            <Route path="/task" element={<TasksPage />} />
            <Route path="/task/archivio" element={<TaskArchivePage />} />
            <Route path="/le-mie-attivita" element={<ActionHubPage />} />
            <Route path="/twint" element={<TwintPage />} />
            <Route path="/task/nuovo" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><TaskEditorPage /></RoleRoute>} />
            <Route path="/carico" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><WorkloadPage /></RoleRoute>} />
            <Route path="/fatture" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><BillingPage /></RoleRoute>} />
            <Route path="/fatture/archivio" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><BillingArchivePage /></RoleRoute>} />
            <Route path="/fatture/condizioni" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><PaymentTermsEditorPage /></RoleRoute>} />
            <Route path="/importa" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><ImportExportPage /></RoleRoute>} />
            <Route path="/utenti" element={<RoleRoute roles={["admin", "amministrazione"]}><UsersPage /></RoleRoute>} />
            <Route path="/competenze" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><CompetenzePage /></RoleRoute>} />
            <Route path="/utenti/nuovo" element={<RoleRoute roles={["admin"]}><UserEditorPage /></RoleRoute>} />
            <Route path="/utenti/:userId/modifica" element={<RoleRoute roles={["admin", "amministrazione"]}><UserEditorPage /></RoleRoute>} />
            <Route path="/registro" element={<RoleRoute roles={["admin"]}><AuditLogPage /></RoleRoute>} />
            <Route path="/registro-contabile" element={<RoleRoute roles={["admin", "amministrazione"]}><AccountingLedgerPage /></RoleRoute>} />
            <Route path="/registro-email" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><EmailLogPage /></RoleRoute>} />
            <Route path="/documenti" element={<MyDocumentsPage />} />
            <Route path="/profilo" element={<ProfilePage />} />
            <Route path="/guida" element={<GuidePage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/disponibilita" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale"]}><AvailabilityPage /></RoleRoute>} />
            <Route path="/notifiche" element={<RoleRoute roles={["artista", "admin", "responsabile", "coordinatore_commerciale", "staff", "amministrazione"]}><NotificationsPage /></RoleRoute>} />
            <Route path="/report" element={<RoleRoute roles={["admin", "responsabile", "coordinatore_commerciale", "amministrazione"]}><ReportsPage /></RoleRoute>} />
            <Route path="/compensi" element={<RoleRoute roles={["admin", "amministrazione", "coordinatore_commerciale", "responsabile", "staff", "artista"]}><CompensationsPage /></RoleRoute>} />
            <Route path="/app-mobile" element={<RoleRoute roles={["admin", "staff", "artista"]}><MobileAppAdminPage /></RoleRoute>} />
            <Route path="/spese" element={<ExpensesPage />} />
            <Route path="/tesoreria" element={<TreasuryPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </NotificationProvider>
      <Toaster richColors position="bottom-right" />
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
